import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../../../services/api";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SyncLoader from "react-spinners/SyncLoader";
import * as Dialog from "@radix-ui/react-dialog";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import { debounce } from "lodash";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IListUsersAdminApiProps from "./dtos/IListUsersAdminApiProps";
import IListUsersAdminDataParams from "./dtos/IListUsersAdminDataParams";
import IFormSearch from "./dtos/IFormSearch";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import DialogModal from "../../../../../../shared/components/DialogModal";
import Button from "../../../../../../shared/components/Button";
import Input from "../../../../../../shared/components/Input";
import IconSvg from "../../../../../../shared/components/IconSvg";
import Pagination from "../../../../../../shared/components/Pagination";
import Tooltip from "../../../../../../shared/components/Tooltip";

import penIcon from "../../../../../../shared/assets/pen-icon.svg";
import blockIcon from "../../../../../../shared/assets/block-icon.svg";
import unblockIcon from "../../../../../../shared/assets/unblock-icon.svg";
import iconSearchWhite from "../../../../../../shared/assets/icon-search-white.svg";
import iconPasswordPassword from "../../../../../../shared/assets/reset-password.svg";

import { Notify } from "../../../../../../shared/utils";

import {
  BoxList,
  ButtonFlexBox,
  ButtonSearch,
  FlexBox,
  HStack,
  Main,
  Form,
  ContainerCreate,
  FlexPagination,
  ContainerLoading,
  Table,
  TableTh,
  TableTr,
  TableTd,
  TableThead,
  TableTbody,
  TableTdText,
} from "./styles";

const ListUsersAdmin: React.FC = () => {
  const location = useLocation();
  const dataLocation: any = location.state;

  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [dataUsersAdminList, setDataUsersAdminList] =
    useState<IListUsersAdminApiProps>({
      pagination: 0,
      dataArray: [],
    });
  const [listUsersAdminDataParams, setListUsersAdminDataParams] =
    useState<IListUsersAdminDataParams>({
      pagination:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.pagination
          : 0,
    });

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionCreate =
    dataUseAuth.admin_user_permissions.create_users_admin;
  const userPermissionRead =
    dataUseAuth.admin_user_permissions.read_users_admin;
  const userPermissionUpdate =
    dataUseAuth.admin_user_permissions.update_users_admin;
  const userPermissionUpdatePassword =
    dataUseAuth.admin_user_permissions.update_users_admin_password;
  const userPermissionBlock =
    dataUseAuth.admin_user_permissions.block_users_admin;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/users-admin");
  }, []);

  const handleCreateUsersAdmin = useCallback(() => {
    navigate("/create-user-admin", {
      state: { params: listUsersAdminDataParams },
    });
  }, [navigate, listUsersAdminDataParams]);

  useEffect(() => {
    (async () => {
      const response = await api.get("/user-admin", {
        params: listUsersAdminDataParams,
      });

      setDataUsersAdminList(response.data);
      setTotalPages(response.data.pagination);
      setLoading(false);
    })();
  }, [reload, listUsersAdminDataParams]);

  const handleBlockUsersAdmin = useCallback(
    async (id: string) => {
      await api.put(`/users/block-user/${id}`);
      setReload(!reload);
    },
    [reload]
  );

  const formSchema = yup.object().shape({
    search: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit = useCallback(
    debounce((formData: string) => {
      try {
        setLoading(true);
        if (formData === "") {
          setListUsersAdminDataParams({
            pagination: 0,
          });
        } else {
          setListUsersAdminDataParams({
            pagination: 0,
            search: formData,
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
      }
    }, 400),
    []
  );

  const navigateView = useCallback(
    (item) => {
      navigate(`/view-user-admin/user-admin/${item.id}`, {
        state: { params: listUsersAdminDataParams },
      });
    },
    [navigate, listUsersAdminDataParams]
  );

  const notifyRead = useCallback(() => {
    Notify({ text: "Você não tem permissão de visualizar", type: "error" });
  }, []);

  const handleInputSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      onSubmit(searchTerm);
    },
    [onSubmit]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Listar usuários admin" />
      <MasterMain>
        <Main>
          <ContainerCreate>
            <Button
              onClick={handleCreateUsersAdmin}
              disabled={!userPermissionCreate}
            >
              Criar usuário admin
            </Button>
          </ContainerCreate>

          <HStack>
            <Input
              onChangeInput={handleInputSearchChange}
              label="search"
              register={register}
              required={true}
              placeholder="Pesquisar"
              style={{ width: "100%" }}
            />
          </HStack>

          <BoxList>
            {loading && (
              <ContainerLoading>
                <SyncLoader loading={loading} color="#663399" size={10} />
              </ContainerLoading>
            )}

            {!loading && (
              <Table>
                <TableThead>
                  <TableTr>
                    <TableTh>Nome</TableTh>
                    <TableTh>Sobrenome</TableTh>
                    <TableTh>E-mail</TableTh>
                    <TableTh>Criado em</TableTh>
                    <TableTh>Opções</TableTh>
                  </TableTr>
                </TableThead>

                <TableTbody>
                  {dataUsersAdminList.dataArray.map((item, index) => (
                    <TableTr key={item.id}>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.first_name}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.last_name}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.email}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>

                      <TableTd>
                        <FlexBox>
                          <Tooltip title="Atualizar senha" placement="top">
                            <ButtonFlexBox
                              disabled={!userPermissionUpdatePassword}
                              onClick={() => {
                                navigate(
                                  `/reset-password-user-admin/${item.id}`,
                                  {
                                    state: { params: listUsersAdminDataParams },
                                  }
                                );
                              }}
                            >
                              <IconSvg
                                width="18"
                                height="18"
                                image={iconPasswordPassword}
                              />
                            </ButtonFlexBox>
                          </Tooltip>

                          <Tooltip title="Atualizar usuário" placement="top">
                            <ButtonFlexBox
                              disabled={!userPermissionUpdate}
                              onClick={() => {
                                navigate(`/edit-user-admin/${item.id}`, {
                                  state: { params: listUsersAdminDataParams },
                                });
                              }}
                            >
                              <IconSvg width="18" height="18" image={penIcon} />
                            </ButtonFlexBox>
                          </Tooltip>

                          <Dialog.Root>
                            <Tooltip
                              title={`${
                                item.is_blocked ? "Desbloquear" : "Bloquear"
                              } usuário`}
                              placement="top"
                            >
                              <Dialog.Trigger asChild>
                                <ButtonFlexBox disabled={!userPermissionBlock}>
                                  <IconSvg
                                    width="18"
                                    height="18"
                                    image={
                                      item.is_blocked ? blockIcon : unblockIcon
                                    }
                                  />
                                </ButtonFlexBox>
                              </Dialog.Trigger>
                            </Tooltip>

                            <DialogModal
                              title={`${
                                item.is_blocked ? "Desbloquear" : "Bloquear"
                              } usuário`}
                              subtitle={`${item.first_name} ${item.last_name}`}
                              type="block"
                              isBlocked={item.is_blocked}
                              callback={() => handleBlockUsersAdmin(item.id)}
                            />
                          </Dialog.Root>
                        </FlexBox>
                      </TableTd>
                    </TableTr>
                  ))}
                </TableTbody>
              </Table>
            )}
          </BoxList>
          <FlexPagination>
            {dataUsersAdminList.dataArray.length > 0 && (
              <Pagination
                total={totalPages}
                offset={listUsersAdminDataParams.pagination}
                setDataParams={setListUsersAdminDataParams}
                dataParams={listUsersAdminDataParams}
              />
            )}
          </FlexPagination>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default ListUsersAdmin;
